import { baseApi } from '@/store/api/baseApi';
import type {
  AttributeValue,
  AttributeValueListParams,
  BulkAttributeValueIdsPayload,
  BulkAttributeValueStatusPayload,
  CreateAttributeValuePayload,
  PaginatedAttributeValues,
  ReorderPayload,
  UpdateAttributeValuePayload,
} from '@/types/attribute-value';

function buildAttributeValueListQuery(params: AttributeValueListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'createdAt');
  searchParams.set('sortOrder', params.sortOrder ?? 'desc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.attributeId) searchParams.set('attributeId', params.attributeId);
  if (params.attributeIds?.trim()) searchParams.set('attributeIds', params.attributeIds.trim());

  return searchParams.toString();
}

export const attributeValuesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAttributeValues: builder.query<PaginatedAttributeValues, AttributeValueListParams>({
      query: (params) => ({
        url: `/attribute-values?${buildAttributeValueListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildAttributeValueListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildAttributeValueListQuery(currentArg ?? {}) !==
        buildAttributeValueListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'AttributeValues' as const, id })),
              { type: 'AttributeValues', id: 'LIST' },
            ]
          : [{ type: 'AttributeValues', id: 'LIST' }],
    }),
    getAttributeValue: builder.query<AttributeValue, string>({
      query: (id) => `/attribute-values/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'AttributeValues', id }],
    }),
    createAttributeValue: builder.mutation<AttributeValue, CreateAttributeValuePayload>({
      query: (body) => ({ url: '/attribute-values/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'AttributeValues', id: 'LIST' }],
    }),
    updateAttributeValue: builder.mutation<
      AttributeValue,
      { id: string; body: UpdateAttributeValuePayload }
    >({
      query: ({ id, body }) => ({ url: `/attribute-values/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'AttributeValues', id },
        { type: 'AttributeValues', id: 'LIST' },
      ],
    }),
    deleteAttributeValue: builder.mutation<void, string>({
      query: (id) => ({ url: `/attribute-values/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'AttributeValues', id: 'LIST' }],
    }),
    reorderAttributeValues: builder.mutation<AttributeValue[], ReorderPayload>({
      query: (body) => ({ url: '/attribute-values/reorder', method: 'POST', body }),
      invalidatesTags: [{ type: 'AttributeValues', id: 'LIST' }],
    }),
    bulkUpdateAttributeValueStatus: builder.mutation<
      { count: number },
      BulkAttributeValueStatusPayload
    >({
      query: (body) => ({ url: '/attribute-values/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'AttributeValues', id: 'LIST' }],
    }),
    bulkDeleteAttributeValues: builder.mutation<{ count: number }, BulkAttributeValueIdsPayload>({
      query: (body) => ({ url: '/attribute-values/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'AttributeValues', id: 'LIST' }],
    }),
    uploadAttributeValueImage: builder.mutation<{ image: string }, FormData>({
      query: (body) => ({ url: '/attribute-values/upload-image', method: 'POST', body }),
    }),
  }),
});

export const {
  useGetAttributeValuesQuery,
  useLazyGetAttributeValuesQuery,
  useGetAttributeValueQuery,
  useCreateAttributeValueMutation,
  useUpdateAttributeValueMutation,
  useDeleteAttributeValueMutation,
  useReorderAttributeValuesMutation,
  useBulkUpdateAttributeValueStatusMutation,
  useBulkDeleteAttributeValuesMutation,
  useUploadAttributeValueImageMutation,
} = attributeValuesApi;
